import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  AlertOctagon,
  ChevronDown,
  ChevronUp,
  X,
  MapPinOff,
  User as UserIcon,
} from 'lucide-react';

/**
 * MapDiagnosticsBanner
 *
 * Renders 0-2 dismissible advisory cards above the Map View based on the
 * diagnostics block returned by `/api/dispatch/route-map`:
 *
 *   1. "Some stops are not visible on the map yet"
 *      — count of stops without lat/lng (geocoding failed, missing address,
 *        or matrix unavailable). Lists affected stops + tech name when known.
 *
 *   2. "Possible bad technician address detected"
 *      — per-tech warning when total drive distance is a statistical outlier
 *        OR a single leg is implausibly long for the service area.
 *
 * Dismissals are session-scoped (sessionStorage), keyed by date so a fix
 * the operator made yesterday won't keep silencing the banner today.
 */
const DISMISS_KEY = (kind, date) => `dispatch.map.diag.dismiss.${kind}.${date}`;

const REASON_COPY = {
  missing_address:        'Customer address is missing',
  geocoding_unavailable:  'Map geocoding service is offline',
  geocoding_failed:       'Address could not be matched by Google',
};

const SUSPICIOUS_REASON_COPY = {
  total_drive_outlier:    'Total drive distance is far longer than this day’s other routes',
  leg_outlier:            'One leg is dramatically longer than the rest of the route',
};

function metersToMiles(m) { return m ? m / 1609.344 : 0; }
function fmtMi(m) {
  const mi = metersToMiles(m);
  if (mi < 0.1) return '0 mi';
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

export default function MapDiagnosticsBanner({ diagnostics, dateKey }) {
  const unmappable = diagnostics?.unmappableStops || [];
  const suspicious = diagnostics?.suspiciousRoutes || [];

  // Session-scoped dismissals keyed per date so corrections don't permanently
  // silence the banner for future days.
  const [dismissUnmappable, setDismissUnmappable] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY('unmappable', dateKey)) === '1'; }
    catch { return false; }
  });
  const [dismissSuspicious, setDismissSuspicious] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY('suspicious', dateKey)) === '1'; }
    catch { return false; }
  });
  const [expanded, setExpanded] = useState(null); // 'unmappable' | 'suspicious' | null

  const handleDismiss = (kind, setter) => {
    setter(true);
    try { sessionStorage.setItem(DISMISS_KEY(kind, dateKey), '1'); } catch (_) { /* quota */ }
  };

  const showUnmappable = unmappable.length > 0 && !dismissUnmappable;
  const showSuspicious = suspicious.length > 0 && !dismissSuspicious;

  // Group unmappable stops by tech for clean per-tech rendering. Stops without
  // a tech (unassigned + no address) go in the trailing "Unassigned" group.
  const groupedUnmappable = useMemo(() => {
    if (!showUnmappable) return [];
    const groups = new Map();
    unmappable.forEach((s) => {
      const key = s.techUid || '__unassigned__';
      const label = s.techName || 'Unassigned';
      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key).items.push(s);
    });
    return Array.from(groups.values());
  }, [unmappable, showUnmappable]);

  if (!showUnmappable && !showSuspicious) return null;

  return (
    <div
      className="px-4 pt-3 space-y-2"
      data-testid="map-diagnostics"
    >
      {showUnmappable && (
        <BannerCard
          tone="amber"
          icon={<MapPinOff className="h-4 w-4" />}
          title={`Some stops are not visible on the map yet`}
          subtitle={`${unmappable.length} assigned stop${unmappable.length === 1 ? '' : 's'} could not be placed because address geocoding is unavailable or incomplete.`}
          expanded={expanded === 'unmappable'}
          onToggle={() => setExpanded(expanded === 'unmappable' ? null : 'unmappable')}
          onDismiss={() => handleDismiss('unmappable', setDismissUnmappable)}
          testId="map-banner-unmappable"
        >
          <div className="mt-3 space-y-3">
            <PossibleCausesList
              items={[
                'Google Geocoding API is not enabled in your Google Cloud project',
                'A customer address is missing one of: street, city, or state',
                'Google could not match the address (typo, new construction, etc.)',
                'A technician’s start or end address is invalid',
              ]}
            />
            <RecommendedActionsList
              items={[
                'Enable the Google Geocoding API on the project that owns your Maps key',
                'Review the affected technician start/end addresses (Settings → Team)',
                'Open the lead’s profile and correct the service address',
                'Click Refresh on the Map View after applying changes',
              ]}
            />
            {groupedUnmappable.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-white/60 overflow-hidden">
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-amber-800 border-b border-amber-200 bg-amber-50/70">
                  Affected stops ({unmappable.length})
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-amber-100">
                  {groupedUnmappable.map((g, gi) => (
                    <div key={gi} className="px-3 py-2">
                      <div className="text-[11px] font-semibold text-gray-800 flex items-center gap-1.5">
                        <UserIcon className="h-3 w-3 text-gray-400" />
                        {g.label}
                      </div>
                      <ul className="mt-1.5 space-y-1">
                        {g.items.map((s) => (
                          <li
                            key={s.taskId}
                            className="text-[11px] text-gray-700 flex items-start gap-2 leading-snug"
                            data-testid={`map-unmappable-stop-${s.taskId}`}
                          >
                            <span className="text-amber-700 mt-0.5">•</span>
                            <span className="flex-1 min-w-0">
                              <span className="font-medium text-gray-900">{s.clientName || 'Customer'}</span>
                              <span className="text-gray-500"> — {s.address || 'no address on file'}</span>
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                              {REASON_COPY[s.reason] || s.reason}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </BannerCard>
      )}

      {showSuspicious && (
        <BannerCard
          tone="rose"
          icon={<AlertOctagon className="h-4 w-4" />}
          title="Possible bad technician address detected"
          subtitle={`${suspicious.length} route${suspicious.length === 1 ? ' is' : 's are'} unusually long for the service area. Please verify the technician start or end location.`}
          expanded={expanded === 'suspicious'}
          onToggle={() => setExpanded(expanded === 'suspicious' ? null : 'suspicious')}
          onDismiss={() => handleDismiss('suspicious', setDismissSuspicious)}
          testId="map-banner-suspicious"
        >
          <div className="mt-3 rounded-md border border-rose-200 bg-white/60 overflow-hidden">
            <div className="divide-y divide-rose-100">
              {suspicious.map((r) => (
                <div
                  key={r.techUid}
                  className="px-3 py-2.5"
                  data-testid={`map-suspicious-tech-${r.techUid}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[12px] font-semibold text-gray-900 flex items-center gap-1.5">
                      <UserIcon className="h-3 w-3 text-gray-400" />
                      {r.techName}
                    </div>
                    <div className="text-[11px] font-bold text-rose-800">
                      {fmtMi(r.totalDriveMeters)} total
                    </div>
                  </div>
                  <ul className="mt-1.5 text-[11px] text-gray-700 leading-snug list-disc list-inside space-y-0.5">
                    {(r.reasons || []).map((rs) => (
                      <li key={rs}>{SUSPICIOUS_REASON_COPY[rs] || rs}</li>
                    ))}
                    {r.startAddress && (
                      <li className="text-gray-500">
                        Current start: <span className="font-mono text-[10.5px] text-gray-700">{r.startAddress}</span>
                      </li>
                    )}
                    {r.endAddress && r.endAddress !== r.startAddress && (
                      <li className="text-gray-500">
                        Current end: <span className="font-mono text-[10.5px] text-gray-700">{r.endAddress}</span>
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </BannerCard>
      )}
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function BannerCard({ tone, icon, title, subtitle, expanded, onToggle, onDismiss, testId, children }) {
  const palette = tone === 'rose'
    ? {
        ring: 'ring-rose-200',
        bg: 'bg-gradient-to-r from-rose-50 via-rose-50 to-white',
        chipBg: 'bg-rose-100',
        chipText: 'text-rose-800',
        titleText: 'text-rose-950',
        subText: 'text-rose-900/80',
        accent: 'text-rose-700',
      }
    : {
        ring: 'ring-amber-200',
        bg: 'bg-gradient-to-r from-amber-50 via-amber-50 to-white',
        chipBg: 'bg-amber-100',
        chipText: 'text-amber-800',
        titleText: 'text-amber-950',
        subText: 'text-amber-900/80',
        accent: 'text-amber-700',
      };

  return (
    <div
      className={`rounded-xl ${palette.bg} ring-1 ${palette.ring} shadow-sm animate-in fade-in slide-in-from-top-1 duration-300`}
      data-testid={testId}
    >
      <div className="p-3 flex items-start gap-3">
        <div className={`h-7 w-7 rounded-full ${palette.chipBg} ${palette.chipText} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-[13px] font-bold ${palette.titleText} leading-snug`}>{title}</h4>
          <p className={`text-[11.5px] ${palette.subText} mt-0.5 leading-relaxed`}>{subtitle}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggle}
            className={`h-7 px-2 rounded-md text-[11px] font-semibold ${palette.chipBg} ${palette.chipText} hover:opacity-80 transition-all flex items-center gap-1`}
            data-testid={`${testId}-toggle`}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide' : 'Details'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className={`h-7 w-7 rounded-md ${palette.chipText} hover:bg-white/60 transition-colors flex items-center justify-center`}
            data-testid={`${testId}-dismiss`}
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 -mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
}

function PossibleCausesList({ items }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3 text-amber-600" />
        Possible causes
      </div>
      <ul className="text-[11px] text-gray-700 space-y-0.5 list-disc list-inside">
        {items.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    </div>
  );
}

function RecommendedActionsList({ items }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
        Recommended actions
      </div>
      <ol className="text-[11px] text-gray-700 space-y-0.5 list-decimal list-inside">
        {items.map((a, i) => <li key={i}>{a}</li>)}
      </ol>
    </div>
  );
}
